import React, { useContext, useEffect, useState } from 'react';
import CbmContext from '../Context/Cbm/cbmContext';
import AdminContext from '../Context/Admin/adminContext';
import { Navigate, useSearchParams } from 'react-router-dom';
import authService from '../Services/authService';
import AdminDashboard from '../Components/Admin/Dashboard/AdminDashboard';
import UserManagement from '../Components/Admin/UserManagement/UserManagement';
import StoreManagement from '../Components/Admin/StoreManagement/StoreManagement';
import SessionManagement from '../Components/Admin/SessionManagement/SessionManagement';
import FormsManagement from '../Components/Admin/FormsManagement/FormsManagement';
import DistrictManagement from '../Components/Admin/DistrictManagement/DistrictManagement';
import usePermissions from '../hooks/usePermissions';
import './AdminNew.css';
import { getBackendUrl } from '../config/apiConfig';
import { getFormsCount } from '../Services/adminService';

const Admin = () => {
  const cbmContext = useContext(CbmContext);
  const { validateToken, authLoading, getStores, stores } = cbmContext;
  const adminContext = useContext(AdminContext);
  const { submittedForms, formLoading } = adminContext;

  // Use centralized permissions hook
  const {
    portalUser,
    hasAnyAccess,
    isAdmin: hasAdminPermission,
    isOffice,
    isDM,
    canManageDistricts,
  } = usePermissions();

  const [queryParams] = useSearchParams();
  const [formName, setFormName] = useState(queryParams.get('form'));
  const [chosenForm, setChosenForm] = useState(queryParams.get('chosenForm'));
  const [links, setLinks] = useState([
    {
      title: 'Admin',
      link: '/admin',
      page: 'admin',
    },
  ]);

  const [currPage, setCurrPage] = useState('dashboard');
  const [formsCount, setFormsCount] = useState(null);

  useEffect(() => {
    if (
      portalUser !== null &&
      (hasAdminPermission || isOffice || isDM) &&
      (stores === null || stores.length === 0)
    ) {
      getStores(portalUser.district);
    }
  }, [getStores, portalUser, stores, hasAdminPermission, isOffice, isDM]);

  useEffect(() => {
    if (
      queryParams.get('form') !== null &&
      queryParams.get('chosenForm') === null
    ) {
      setFormName(queryParams.get('form'));
    } else {
      setFormName(null);
    }

    if (
      queryParams.get('chosenForm') !== null &&
      queryParams.get('form') !== null
    ) {
      setChosenForm(queryParams.get('chosenForm'));
      setFormName(queryParams.get('form'));
    } else {
      setChosenForm(null);
    }

    if (
      queryParams.get('form') === null &&
      queryParams.get('chosenForm') === null
    ) {
      setFormName(null);
    }

    if (queryParams.get('currPage') !== null) {
      setCurrPage(queryParams.get('currPage'));
    } else if (!queryParams.get('currPage') && !currPage) {
      // Only set default if currPage is not already set
      setCurrPage('dashboard');
    }
  }, [
    queryParams,
    portalUser,
    formName,
    chosenForm,
    links,
    setLinks,
    setFormName,
    setChosenForm,
    setCurrPage,
    currPage,
  ]);

  useEffect(() => {
    // Check if should logout (e.g., after 401 error)
    // Note: The actual redirect is handled in the render phase below
    // This effect just validates the token when needed
    if (authLoading === false && formLoading === false && portalUser === null) {
      const token = authService.getToken();
      console.log(
        'Validating token, has token:',
        !!token,
        'has refreshToken:',
        !!authService.getRefreshToken()
      );
      validateToken(token);
    }
  }, [
    authLoading,
    portalUser,
    validateToken,
    formLoading,
    submittedForms,
    formName,
  ]);

  // Fetch aggregate forms count for dashboard via new endpoint
  useEffect(() => {
    if (currPage === 'dashboard') {
      const baseUrl = getBackendUrl();
      (async () => {
        try {
          const { count, error } = await getFormsCount(baseUrl);
          if (!error) setFormsCount(count);
        } catch (e) {
          console.warn('Failed to fetch forms count for dashboard', e);
        }
      })();
    }
  }, [currPage]);

  // Check logout flag first - redirect immediately if set
  if (authService.shouldLogout() || !authService.getToken()) {
    console.log(
      'Admin: Redirecting to logout - shouldLogout:',
      authService.shouldLogout(),
      'hasToken:',
      !!authService.getToken()
    );
    return <Navigate to="/logout" />;
  }

  // Redirect if user doesn't have any admin access
  if (portalUser && !hasAnyAccess) {
    return <Navigate to="/" />;
  }

  return (
    <div className="admin-new-layout">
      {/* Modern Tab Navigation */}
      <div className="admin-nav-tabs">
        <button
          className={`nav-tab ${currPage === 'dashboard' ? 'active' : ''}`}
          onClick={() => setCurrPage('dashboard')}
        >
          <span className="tab-icon">📊</span>
          Dashboard
        </button>
        {hasAdminPermission && (
          <button
            className={`nav-tab ${currPage === 'users' ? 'active' : ''}`}
            onClick={() => setCurrPage('users')}
          >
            <span className="tab-icon">👥</span>
            Users
          </button>
        )}
        {(hasAdminPermission || isOffice || isDM) && (
          <button
            className={`nav-tab ${currPage === 'stores' ? 'active' : ''}`}
            onClick={() => setCurrPage('stores')}
          >
            <span className="tab-icon">🏪</span>
            Stores
          </button>
        )}
        {canManageDistricts && (
          <button
            className={`nav-tab ${
              currPage === 'districts' || currPage === 'subareas'
                ? 'active'
                : ''
            }`}
            onClick={() => setCurrPage('districts')}
          >
            <span className="tab-icon">🗺️</span>
            Districts
          </button>
        )}
        <button
          className={`nav-tab ${currPage === 'forms' ? 'active' : ''}`}
          onClick={() => setCurrPage('forms')}
        >
          <span className="tab-icon">📋</span>
          Forms
        </button>
        {hasAdminPermission && (
          <button
            className={`nav-tab ${currPage === 'sessions' ? 'active' : ''}`}
            onClick={() => setCurrPage('sessions')}
          >
            <span className="tab-icon">🔒</span>
            Sessions
          </button>
        )}
      </div>

      {/* Content Area */}
      <div className="admin-content-area">
        {currPage === 'dashboard' && (
          <AdminDashboard onNavigate={setCurrPage} formsCount={formsCount} />
        )}
        {currPage === 'users' && hasAdminPermission && <UserManagement />}
        {currPage === 'users' && !hasAdminPermission && (
          <div className="permission-denied">
            <h2>Access Denied</h2>
            <p>Only Admin users can manage portal users.</p>
          </div>
        )}
        {currPage === 'stores' && <StoreManagement />}
        {(currPage === 'districts' || currPage === 'subareas') &&
          canManageDistricts && <DistrictManagement />}
        {(currPage === 'districts' || currPage === 'subareas') &&
          !canManageDistricts && (
            <div className="permission-denied">
              <h2>Access Denied</h2>
              <p>
                Only Admin and Office users can manage districts and sub-areas.
              </p>
            </div>
          )}
        {currPage === 'forms' && <FormsManagement />}
        {currPage === 'sessions' && hasAdminPermission && <SessionManagement />}
        {currPage === 'sessions' && !hasAdminPermission && (
          <div className="permission-denied">
            <h2>Access Denied</h2>
            <p>Only Admin users can manage sessions.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Admin;
